import {
  CC_ESTEIRA_STEPS,
  contrFornEventoRefDate,
  dateInYear,
  pctDistribuido,
  pctDoOrcado,
  resolveCcIdFromItemOuRubr,
} from './budgetDashboardHelpers'

export const DRILL_TYPES = {
  CC: 'cc',
  RUBRICA: 'rubrica',
  SUBRUBRICA: 'subrubrica',
  ITEM: 'item',
}

const emptyEsteira = () => ({
  orcado: 0,
  distribuido: 0,
  programado: 0,
  comprometido: 0,
  liberado: 0,
  pago: 0,
})

function sumInto(target, source) {
  for (const step of CC_ESTEIRA_STEPS) {
    target[step.key] += Number(source[step.key] || 0)
  }
}

function enrichCard(row) {
  const pct = pctDistribuido(row.orcado, row.distribuido)
  const estouroFromDistrib = row.orcado > 0 && row.distribuido > row.orcado
  const disponivel = row.orcado - row.comprometido
  return {
    ...row,
    pctDistribuido: pct,
    pctProgramado: pctDoOrcado(row.orcado, row.programado),
    pctComprometido: pctDoOrcado(row.orcado, row.comprometido),
    pctLiberado: pctDoOrcado(row.orcado, row.liberado),
    pctPago: pctDoOrcado(row.orcado, row.pago),
    disponivel,
    pctDisponivel: pctDoOrcado(row.orcado, disponivel),
    estouro: row.estouro != null ? row.estouro : estouroFromDistrib,
    disponivelDistribuir: row.orcado - row.distribuido,
    qtdContratos: Number(row.qtdContratos || 0),
  }
}

function resolveRubricaFromTax(tax = {}, orcItemId) {
  const nome = String(tax.nivel3 || tax.rubrica || '').trim()
  const id = Number(tax.nivel3_id || 0) || (nome ? nome : `nr-${orcItemId}`)
  return { rubricaId: id, rubricaNome: nome || 'Sem rubrica' }
}

function resolveSubrubricaFromTax(tax = {}, orcItemId) {
  const nome = String(tax.nivel4 || '').trim()
  const id = Number(tax.subrubrica_id || tax.id_oikos || 0) || (nome ? nome : `ns-${orcItemId}`)
  return { subrubricaId: id, subrubricaNome: nome || 'Sem subrubrica' }
}

function applyDotAnualTaxonomy(item, row) {
  if (!row) return
  if (row.nivel3 || row.nivel3_id) {
    const rub = resolveRubricaFromTax({
      nivel3: row.nivel3,
      nivel3_id: row.nivel3_id,
      rubrica: row.nivel3,
    }, item.orcItemId)
    item.rubricaId = rub.rubricaId
    item.rubricaNome = rub.rubricaNome
  }
  if (row.nivel4 || row.subrubrica_id) {
    const sub = resolveSubrubricaFromTax({
      nivel4: row.nivel4,
      subrubrica_id: row.subrubrica_id,
    }, item.orcItemId)
    item.subrubricaId = sub.subrubricaId
    item.subrubricaNome = sub.subrubricaNome
  }
}

export function buildItemEsteiraRows({
  ano,
  dotAnualRows,
  orcDotRows,
  eventoRows,
  bdroRows,
  movRows,
  taxonomyRows,
  contratosPorItem = {},
}) {
  const taxByItem = {}
  for (const row of taxonomyRows || []) {
    taxByItem[Number(row.orcItem_id)] = row
  }

  const facts = {}

  const ensure = (orcItemId) => {
    const id = Number(orcItemId)
    if (!id) return null
    if (!facts[id]) {
      const tax = taxByItem[id] || {}
      const rub = resolveRubricaFromTax(tax, id)
      const sub = resolveSubrubricaFromTax(tax, id)
      facts[id] = {
        orcItemId: id,
        ccId: Number(tax.nivel2_id || 0),
        ccNome: tax.nivel2 || '',
        rubricaId: rub.rubricaId,
        rubricaNome: rub.rubricaNome,
        subrubricaId: sub.subrubricaId,
        subrubricaNome: sub.subrubricaNome,
        itemLabel: tax.item_label || tax.orcItem_desc || `Item #${id}`,
        qtdItens: 1,
        qtdContratos: Number(contratosPorItem[id] || 0),
        ...emptyEsteira(),
      }
    }
    return facts[id]
  }

  for (const row of dotAnualRows || []) {
    const item = ensure(row.orcItem_id)
    if (!item) continue
    applyDotAnualTaxonomy(item, row)
    const vlrIni = row.orcDotAnual_vlrIni
    const vlrAtu = row.orcDotAnual_vlrAtu
    item.orcado += Number(vlrIni != null && vlrIni !== '' ? vlrIni : vlrAtu || 0)
    item.distribuido += Number(vlrAtu || 0)
    if (!item.ccId && row.cc_id) {
      item.ccId = Number(row.cc_id)
      item.ccNome = row.cc_nome || item.ccNome
    }
  }

  for (const row of orcDotRows || []) {
    if (!dateInYear(row.orcDot_data, ano)) continue
    const item = ensure(row.orcItem_id)
    if (!item) continue
    item.programado += Number(row.orcDot_valor || 0)
  }

  for (const row of eventoRows || []) {
    if (Number(row.contrForn_ativo) !== 1) continue
    if (!dateInYear(contrFornEventoRefDate(row), ano)) continue
    const item = ensure(row.orcItem_id)
    if (!item) continue
    item.comprometido += Number(row.contrFornEvento_vlr || 0)
  }

  for (const row of bdroRows || []) {
    if (!dateInYear(row.bdro_dtVenc, ano)) continue
    const itemId = Number(row.orcItem_id)
    if (itemId) {
      const item = ensure(itemId)
      if (item) item.liberado += Math.abs(Number(row.bdro_vlr || 0))
    }
  }

  for (const row of movRows || []) {
    if (!dateInYear(row.mov_dtQuit, ano)) continue
    if (Number(row.mov_vlr || 0) >= 0) continue
    const itemId = Number(row.orcItem_id)
    if (itemId) {
      const item = ensure(itemId)
      if (item) item.pago += Math.abs(Number(row.mov_vlr || 0))
    }
  }

  return Object.values(facts).filter(item => (
    CC_ESTEIRA_STEPS.some(step => item[step.key] > 0)
  ))
}

function aggregateGroup(items, config) {
  const groups = new Map()
  for (const item of items) {
    const groupId = config.getGroupId(item)
    if (!groups.has(groupId)) {
      groups.set(groupId, {
        cardType: config.cardType,
        cardId: `${config.cardType}-${groupId}`,
        cardNome: config.getGroupLabel(item, groupId),
        ccId: item.ccId,
        ccNome: item.ccNome,
        rubricaId: item.rubricaId,
        rubricaNome: item.rubricaNome,
        subrubricaId: item.subrubricaId,
        subrubricaNome: item.subrubricaNome,
        orcItemId: config.cardType === DRILL_TYPES.ITEM ? item.orcItemId : undefined,
        qtdItens: 0,
        qtdContratos: 0,
        ...emptyEsteira(),
      })
    }
    const group = groups.get(groupId)
    group.qtdItens += 1
    group.qtdContratos += Number(item.qtdContratos || 0)
    sumInto(group, item)
  }
  return [...groups.values()]
    .map(enrichCard)
    .sort((a, b) => a.cardNome.localeCompare(b.cardNome, 'pt-BR'))
}

function filterItemsByPath(items, drillPath) {
  let filtered = items
  for (const node of drillPath) {
    if (node.type === DRILL_TYPES.CC) {
      filtered = filtered.filter(i => i.ccId === Number(node.id))
    } else if (node.type === DRILL_TYPES.RUBRICA) {
      filtered = filtered.filter(i => String(i.rubricaId) === String(node.id))
    } else if (node.type === DRILL_TYPES.SUBRUBRICA) {
      filtered = filtered.filter(i => String(i.subrubricaId) === String(node.id))
    }
  }
  return filtered
}

export function getDrillLevel(drillPath) {
  if (!drillPath?.length) return DRILL_TYPES.CC
  const last = drillPath[drillPath.length - 1].type
  if (last === DRILL_TYPES.CC) return DRILL_TYPES.RUBRICA
  if (last === DRILL_TYPES.RUBRICA) return DRILL_TYPES.SUBRUBRICA
  if (last === DRILL_TYPES.SUBRUBRICA) return DRILL_TYPES.ITEM
  return DRILL_TYPES.ITEM
}

export function buildDrillCards({ drillPath, itemRows, ccSummaries }) {
  const level = getDrillLevel(drillPath)

  if (level === DRILL_TYPES.CC) {
    const contratosByCc = {}
    for (const item of itemRows || []) {
      if (!item.ccId) continue
      contratosByCc[item.ccId] = (contratosByCc[item.ccId] || 0) + Number(item.qtdContratos || 0)
    }
    return (ccSummaries || []).map(cc => enrichCard({
      cardType: DRILL_TYPES.CC,
      cardId: `${DRILL_TYPES.CC}-${cc.ccId}`,
      cardNome: cc.ccNome,
      ccId: cc.ccId,
      ccNome: cc.ccNome,
      qtdItens: cc.qtdItens,
      qtdContratos: contratosByCc[cc.ccId] || 0,
      tetoCc: cc.tetoCc,
      estouro: cc.estouro,
      orcado: cc.orcado,
      distribuido: cc.distribuido,
      programado: cc.programado,
      comprometido: cc.comprometido,
      liberado: cc.liberado,
      pago: cc.pago,
      disponivelDistribuir: cc.disponivelDistribuir,
      pctDistribuido: cc.pctDistribuido,
    }))
  }

  const scoped = filterItemsByPath(itemRows || [], drillPath)

  if (level === DRILL_TYPES.RUBRICA) {
    return aggregateGroup(scoped, {
      cardType: DRILL_TYPES.RUBRICA,
      getGroupId: item => String(item.rubricaId),
      getGroupLabel: item => item.rubricaNome,
    })
  }

  if (level === DRILL_TYPES.SUBRUBRICA) {
    const rubricaItems = scoped
    const distinctSub = new Set(rubricaItems.map(i => String(i.subrubricaId)))
    if (distinctSub.size <= 1 && rubricaItems.length > 0) {
      return aggregateGroup(rubricaItems, {
        cardType: DRILL_TYPES.ITEM,
        getGroupId: item => String(item.orcItemId),
        getGroupLabel: item => item.itemLabel,
      })
    }
    return aggregateGroup(scoped, {
      cardType: DRILL_TYPES.SUBRUBRICA,
      getGroupId: item => String(item.subrubricaId),
      getGroupLabel: item => item.subrubricaNome,
    })
  }

  return aggregateGroup(scoped, {
    cardType: DRILL_TYPES.ITEM,
    getGroupId: item => String(item.orcItemId),
    getGroupLabel: item => item.itemLabel,
  })
}

export function buildDrillNodeFromCard(card) {
  if (card.cardType === DRILL_TYPES.CC) {
    return { type: DRILL_TYPES.CC, id: card.ccId, label: card.cardNome }
  }
  if (card.cardType === DRILL_TYPES.RUBRICA) {
    return { type: DRILL_TYPES.RUBRICA, id: card.rubricaId, label: card.cardNome }
  }
  if (card.cardType === DRILL_TYPES.SUBRUBRICA) {
    return { type: DRILL_TYPES.SUBRUBRICA, id: card.subrubricaId, label: card.cardNome }
  }
  return null
}

export function drillPathLabel(drillPath) {
  return (drillPath || []).map(n => n.label).join(' › ')
}

export function hasDrillEsteiraData(card) {
  if (!card) return false
  if (card.qtdItens > 0) return true
  return CC_ESTEIRA_STEPS.some(step => Number(card[step.key] || 0) !== 0)
}
